import { Link, useRouterState } from "@tanstack/react-router";
import { LayoutDashboard, Store, Plus, Layers, Activity, Shield } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { useEffect, useState } from "react";
import { isAdminWallet } from "@/config/robotdex";
import logoAsset from "@/assets/robotdex-logo.jpg.asset.json";

const ADDRESS_KEY = "robotdex.lastAddress";
const ADDRESS_EVENT = "robotdex:wallet-address";

function readStoredAddress(): `0x${string}` | undefined {
  if (typeof window === "undefined") return undefined;
  const address = window.localStorage.getItem(ADDRESS_KEY);
  return address?.startsWith("0x") ? (address as `0x${string}`) : undefined;
}

function useStoredAddress(): `0x${string}` | undefined {
  const [address, setAddress] = useState<`0x${string}` | undefined>(undefined);

  useEffect(() => {
    const sync = () => setAddress(readStoredAddress());
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(ADDRESS_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(ADDRESS_EVENT, sync);
    };
  }, []);

  return address;
}

const items = [
  { title: "Home", url: "/", icon: LayoutDashboard, exact: true },
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Market", url: "/market", icon: Store },
  { title: "Create", url: "/create", icon: Plus },
  { title: "Collections", url: "/collections", icon: Layers },
  { title: "Activity", url: "/activity", icon: Activity },
];

export function AppSidebar() {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const address = useStoredAddress();
  const showAdmin = isAdminWallet(address);

  // Auto-close the mobile sidebar when navigating between routes.
  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [pathname, isMobile, setOpenMobile]);

  const isActive = (path: string) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-3">
        <Link to="/" className="flex items-center gap-2">
          <img src={logoAsset.url} alt="RobotDex" className="h-8 w-8 rounded object-cover" />
          {!collapsed && (
            <span className="text-lg font-bold tracking-wide neon-text">RobotDex</span>
          )}
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Marketplace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url} className="flex items-center gap-2">
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Platform</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/admincontrol2026")}>
                    <Link to="/admincontrol2026" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      {!collapsed && <span>Platform Panel</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
